'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

type LayoutMode = 'default' | 'compact' | 'comfortable';

interface LayoutContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  isCompact: boolean;
  isComfortable: boolean;
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  padding: {
    card: string;
    section: string;
    container: string;
    button: string;
  };
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

const layoutConfigs = {
  default: {
    spacing: {
      xs: '0.5rem',    // 8px
      sm: '1rem',      // 16px
      md: '1.5rem',    // 24px
      lg: '2rem',      // 32px
      xl: '3rem',      // 48px
      '2xl': '4rem',   // 64px
    },
    padding: {
      card: '1.5rem',      // 24px
      section: '2rem',     // 32px
      container: '1rem',   // 16px
      button: '0.75rem 1.5rem', // 12px 24px
    },
    fontSize: {
      xs: '0.75rem',   // 12px
      sm: '0.875rem',  // 14px
      base: '1rem',    // 16px
      lg: '1.125rem',  // 18px
      xl: '1.25rem',   // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
    },
    borderRadius: {
      sm: '0.25rem',   // 4px
      md: '0.375rem',  // 6px
      lg: '0.5rem',    // 8px
      xl: '0.75rem',   // 12px
    },
  },
  compact: {
    spacing: {
      xs: '0.25rem',   // 4px
      sm: '0.5rem',    // 8px
      md: '0.75rem',   // 12px
      lg: '1rem',      // 16px
      xl: '1.5rem',    // 24px
      '2xl': '2rem',   // 32px
    },
    padding: {
      card: '1rem',        // 16px
      section: '1.25rem',  // 20px
      container: '0.75rem', // 12px
      button: '0.5rem 1rem', // 8px 16px
    },
    fontSize: {
      xs: '0.6875rem', // 11px
      sm: '0.75rem',   // 12px
      base: '0.875rem', // 14px
      lg: '1rem',      // 16px
      xl: '1.125rem',  // 18px
      '2xl': '1.25rem', // 20px
      '3xl': '1.5rem',  // 24px
    },
    borderRadius: {
      sm: '0.1875rem', // 3px
      md: '0.25rem',   // 4px
      lg: '0.375rem',  // 6px
      xl: '0.5rem',    // 8px
    },
  },
  comfortable: {
    spacing: {
      xs: '0.75rem',   // 12px
      sm: '1.5rem',    // 24px
      md: '2rem',      // 32px
      lg: '3rem',      // 48px
      xl: '4rem',      // 64px
      '2xl': '6rem',   // 96px
    },
    padding: {
      card: '2rem',        // 32px
      section: '3rem',     // 48px
      container: '1.5rem', // 24px
      button: '1rem 2rem', // 16px 32px
    },
    fontSize: {
      xs: '0.875rem',  // 14px
      sm: '1rem',      // 16px
      base: '1.125rem', // 18px
      lg: '1.25rem',   // 20px
      xl: '1.5rem',    // 24px
      '2xl': '1.875rem', // 30px
      '3xl': '2.25rem',   // 36px
    },
    borderRadius: {
      sm: '0.375rem',  // 6px
      md: '0.5rem',    // 8px
      lg: '0.75rem',   // 12px
      xl: '1rem',      // 16px
    },
  },
};

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('default');

  // Initialize layout from localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('layoutMode') as LayoutMode | null;
    if (savedLayout && ['default', 'compact', 'comfortable'].includes(savedLayout)) {
      setLayoutMode(savedLayout);
    }
  }, []);

  // Save layout preference
  const handleSetLayoutMode = (mode: LayoutMode) => {
    setLayoutMode(mode);
    localStorage.setItem('layoutMode', mode);
    
    // Apply layout classes to document
    document.documentElement.classList.remove('layout-default', 'layout-compact', 'layout-comfortable');
    document.documentElement.classList.add(`layout-${mode}`);
  };

  const currentConfig = layoutConfigs[layoutMode];
  const isCompact = layoutMode === 'compact';
  const isComfortable = layoutMode === 'comfortable';

  const contextValue: LayoutContextType = {
    layoutMode,
    setLayoutMode: handleSetLayoutMode,
    isCompact,
    isComfortable,
    spacing: currentConfig.spacing,
    padding: currentConfig.padding,
    fontSize: currentConfig.fontSize,
    borderRadius: currentConfig.borderRadius,
  };

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
}; 